import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  gameRunId: null,
  player: null,
  group: null,
  members: [],
  gameStatus: null, // The current, active status of the game
  pendingGameStatus: null, // A new status received from the server, to be applied at the end of the round
  isTurnPlayer: false,
  error: null,
  loading: false,
  token: null, // This will store the JWT authentication token
  gameStarted: false, // Flag to indicate the game has started
  currentGamePoint: null,
  selectedPartner: null,
  gameToken: null,
  turnNumber: 1,
  selectedExample: null, // New state property
  gameScale: null, // New state property
  turnPlayer: null, // New state property
  currentScreen: 'waiting', // Current screen for reconnection handling
};

export const runningGameSlice = createSlice({
  name: 'runningGame',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action) => {
      state.loading = false;
      // Merge player data with game_group_member data (for is_paused, is_active_player)
      state.player = {
        ...action.payload.player,
        is_paused: action.payload.game_group_member?.is_paused || false,
        is_active_player: action.payload.game_group_member?.is_active_player || false,
        is_ready: action.payload.game_group_member?.is_ready || false,
      };
      state.group = action.payload.group;
      state.members = action.payload.members || [];
      state.gameRunId = action.payload.game_run_id; // FIX: Use snake_case from backend payload
      state.gameToken = action.payload.game_token;
      state.token = action.payload.token;
      state.isTurnPlayer = action.payload.game_group_member?.is_active_player || false;
      state.gameStatus = action.payload.status; // Set initial status from login
      state.pendingGameStatus = null; // Reset any pending status on new login
      // Store current screen from game_group_member for reconnection handling
      state.currentScreen = action.payload.game_group_member?.current_screen || 'waiting';
      // Store example and scale if provided (for reconnection on sector-selection)
      if (action.payload.selected_example) {
        state.selectedExample = action.payload.selected_example;
      }
      if (action.payload.game_scale) {
        state.gameScale = action.payload.game_scale;
      }
      state.error = null;
    },
    loginFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // This action applies the pending status, making it the current status
    setGameStatus: (state) => {
      if (state.pendingGameStatus) {
        state.gameStatus = state.pendingGameStatus;
        state.pendingGameStatus = null; // Clear the pending status after applying it
      }
    },
    // This new action sets a status update that is waiting to be applied
    setPendingGameStatus: (state, action) => {
      state.pendingGameStatus = action.payload;
    },
    updateMembers: (state, action) => {
      state.members = action.payload.members;
    },
    setTurnPlayer: (state, action) => {
      const nextPlayerId = action.payload.next_player_id;
      state.isTurnPlayer = state.player?.id === nextPlayerId;
      if (state.isTurnPlayer) {
        state.turnPlayer = state.player;
      } else {
        const nextPlayer = state.members.find(m => m.id === nextPlayerId);
        state.turnPlayer = nextPlayer || null;
      }
    },
    startGame: (state, action) => {
      state.isTurnPlayer = action.payload.active_player_id === state.player?.id;
      state.gameStarted = true;
    },
    setGameStarted: (state) => {
      state.gameStarted = true;
    },
    setTurnNumber: (state, action) => {
      state.turnNumber = action.payload;
    },
    setCurrentGamePoint: (state, action) => {
      state.currentGamePoint = action.payload;
    },
    setSelectedPartner: (state, action) => {
      state.selectedPartner = action.payload;
    },
    setPartnerChoiceData: (state, action) => {
      state.selectedExample = action.payload.selectedExample;
      state.gameScale = action.payload.gameScale;
    },
    setPlayerPaused: (state, action) => {
      // Only update current player's pause status if they are the one who paused/resumed
      if (state.player && action.payload.paused_player_id === state.player.id) {
        state.player.is_paused = action.payload.is_paused;
      }
      // Update members list with new pause status (for all players)
      if (action.payload.members) {
        state.members = action.payload.members;
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    logout: () => initialState,
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  setGameStatus,
  setPendingGameStatus,
  updateMembers,
  setTurnPlayer,
  startGame,
  setGameStarted,
  setCurrentGamePoint,
  setSelectedPartner,
  setPartnerChoiceData,
  setTurnNumber,
  setPlayerPaused,
  clearError,
  logout,
} = runningGameSlice.actions;

export default runningGameSlice.reducer;
